import { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HorizontalBarChart,
  HorizontalBarDatum,
  OTHER_BAR_COLOR,
} from '../../../components/HorizontalBarChart/HorizontalBarChart';
import { BreakdownsContext } from './contexts/BreakdownsContext';
import { BreakdownsContextType } from './contexts/BreakdownsContextTypes';
import { BreakdownsContainer } from '../../../components/BreakdownsContainer/BreakdownsContainer';

interface Country {
  id: number;
  name: string;
  actors_count: number;
  publications_count: number;
}

type CountriesData = Country[];

interface CountriesBarChartProps {
  narrative: { id: number };
  isShare?: boolean;
  mode?: 'actors' | 'publications';
  isClickable?: boolean;
}

export const StateAffiliatedCountriesChart = ({
  mode = 'actors',
  isShare = false,
  isClickable = true,
}: CountriesBarChartProps) => {
  const { t } = useTranslation();
  const {
    stateAffiliated,
    openModal,
    setAffiliationCountryId,
    setCurrentWidgetName,
    setAffiliationCountriesExcludeFilter,
    setTypesFilter,
    setModalTitle,
  } = useContext(BreakdownsContext) as BreakdownsContextType;
  const [countriesData, setCountriesData] = useState<CountriesData | null>(
    null,
  );
  const [currentMode, setCurrentMode] = useState<'actors' | 'publications'>(
    mode,
  );
  const [actorsData, setActorsData] = useState<HorizontalBarDatum[] | null>(null);
  const [publicationsData, setPublicationsData] = useState<HorizontalBarDatum[] | null>(null);

  useEffect(() => {
    if (!stateAffiliated) return;
    setCountriesData(stateAffiliated.top_countries || []);
  }, [stateAffiliated]);

  const getChartData = (mode: 'actors' | 'publications'): HorizontalBarDatum[] => {
    if (!countriesData?.length) {
      return [];
    }

    const modeCountKey = mode === 'actors' ? 'actors_count' : 'publications_count';
    const otherCountriesCount =
      stateAffiliated?.other_countries?.reduce(
        (accumulator, currentValue) => accumulator + currentValue[modeCountKey],
        0,
      ) || 0;

    const data: HorizontalBarDatum[] = countriesData.map((country) => ({
      name: country.name,
      value: country[modeCountKey],
    }));

    if (otherCountriesCount > 0) {
      data.push({ name: t('Others'), value: otherCountriesCount, color: OTHER_BAR_COLOR });
    }

    return data;
  };

  useEffect(() => {
    if (countriesData) {
      setActorsData(getChartData('actors'));
      setPublicationsData(getChartData('publications'));
    }
  }, [countriesData]);

  const chartData = currentMode === 'actors' ? actorsData : publicationsData;

  return (
    <BreakdownsContainer
      title={t('State-affiliated by countries')}
      details={false}
      flexGrow={3}
      tabs={[
        { name: t('Actors'), value: 'actors' },
        { name: t('Publications'), value: 'publications' },
      ]}
      onTabChange={(tabValue: string | null) => {
        if (tabValue === 'actors' || tabValue === 'publications') {
          setCurrentMode(tabValue);
        }
      }}
    >
      <HorizontalBarChart
        chartData={chartData}
        isClickable={isClickable}
        seriesName={currentMode === 'actors' ? t('Actors') : t('Publications')}
        onSelect={(index) => {
          if (index === null || !countriesData) return;
          let isOthers = countriesData.length < index + 1;
          if (isOthers) {
            let countriesOptionList = stateAffiliated?.other_countries?.map(
              (c) => c.id,
            );
            setTypesFilter({
              value: 'state_affiliated',
              label: 'State-affiliated',
              bool: true,
            });
            setAffiliationCountryId(countriesOptionList);
            setCurrentWidgetName('countries_breakdown');
            setModalTitle(t('Others'));
            openModal();
            return;
          }
          let country = countriesData[index];
          setTypesFilter({
            value: 'state_affiliated',
            label: 'State-affiliated',
            bool: true,
          });
          setAffiliationCountryId([country.id]);
          setCurrentWidgetName('countries_breakdown');
          setModalTitle(country.name);
          openModal();
        }}
      />
    </BreakdownsContainer>
  );
};
